import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Link as RouterLink } from "react-router-dom";
import { UserPlus, Snowflake, Trophy, Check, X, Gift, Swords, Flame } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import api, { formatApiErrorDetail } from "../lib/api";
import { fmtDistance, fmtDuration, todayStr } from "../lib/format";

function AddFriend({ onDone }) {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const send = async () => {
    if (!email) return;
    setLoading(true);
    try {
      const { data } = await api.post("/social/request", { email });
      toast.success(data.status === "accepted" ? "Ihr seid jetzt Freunde!" : "Anfrage gesendet");
      setEmail("");
      onDone();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Fehlgeschlagen");
    } finally {
      setLoading(false);
    }
  };
  return (
    <div className="bg-[#121212] border border-white/10 rounded-2xl p-4 mb-5">
      <div className="data-label mb-2">Freund per E-Mail hinzufügen</div>
      <div className="flex gap-2">
        <input
          data-testid="add-friend-email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="freund@beispiel.de"
          className="flex-1 min-w-0 bg-[#0A0A0A] border border-white/10 rounded-xl px-3 py-3 text-sm focus:border-[#CCFF00] focus:outline-none"
        />
        <button onClick={send} disabled={loading} data-testid="add-friend-submit" className="w-12 h-12 rounded-xl bg-[#CCFF00] flex items-center justify-center shrink-0 active:scale-95 transition-transform disabled:opacity-60">
          <UserPlus className="w-5 h-5 text-black" />
        </button>
      </div>
    </div>
  );
}

function ChallengeModal({ friends, onClose, onCreated }) {
  const [opponent, setOpponent] = useState(friends[0]?.id || "");
  const [metric, setMetric] = useState("distance");
  const [days, setDays] = useState(7);
  const [loading, setLoading] = useState(false);
  const create = async () => {
    setLoading(true);
    try {
      await api.post("/challenges", { opponent_id: opponent, metric, days: Number(days) });
      toast.success("Challenge gesendet!");
      onCreated();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Fehlgeschlagen");
    } finally {
      setLoading(false);
    }
  };
  return (
    <div onClick={onClose} className="fixed inset-0 z-[2000] bg-black/80 backdrop-blur-sm flex items-end sm:items-center justify-center" data-testid="challenge-modal">
      <div onClick={(e) => e.stopPropagation()} className="bg-[#0A0A0A] border-t sm:border border-white/10 rounded-t-3xl sm:rounded-3xl w-full max-w-lg p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-display font-black text-2xl tracking-tighter">Neue Challenge</h2>
          <button onClick={onClose} className="w-10 h-10 rounded-full bg-[#1A1A1A] border border-white/10 flex items-center justify-center"><X className="w-5 h-5" /></button>
        </div>
        <div className="space-y-4">
          <div>
            <label className="data-label block mb-2">Gegner</label>
            <select data-testid="challenge-opponent" value={opponent} onChange={(e) => setOpponent(e.target.value)} className="w-full bg-[#121212] border border-white/10 rounded-2xl px-4 py-3 focus:border-[#CCFF00] focus:outline-none">
              {friends.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
            </select>
          </div>
          <div>
            <label className="data-label block mb-2">Metrik</label>
            <div className="flex gap-2 bg-[#121212] border border-white/10 rounded-full p-1">
              <button onClick={() => setMetric("distance")} className={`flex-1 rounded-full py-2 text-sm font-bold transition-colors ${metric === "distance" ? "bg-[#CCFF00] text-black" : "text-neutral-400"}`}>Distanz</button>
              <button onClick={() => setMetric("duration")} className={`flex-1 rounded-full py-2 text-sm font-bold transition-colors ${metric === "duration" ? "bg-[#CCFF00] text-black" : "text-neutral-400"}`}>Zeit</button>
            </div>
          </div>
          <div>
            <label className="data-label block mb-2">Dauer (Tage)</label>
            <input data-testid="challenge-days" type="number" min={1} max={30} value={days} onChange={(e) => setDays(e.target.value)} className="w-full bg-[#121212] border border-white/10 rounded-2xl px-4 py-3 focus:border-[#CCFF00] focus:outline-none" />
          </div>
          <button onClick={create} disabled={loading || !opponent} data-testid="challenge-create" className="w-full bg-[#CCFF00] text-black font-display font-black text-lg rounded-full py-4 active:scale-95 transition-transform disabled:opacity-60">
            {loading ? "Senden..." : "Challenge starten"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Friends() {
  const [friends, setFriends] = useState([]);
  const [requests, setRequests] = useState({ incoming: [], outgoing: [] });
  const [challenges, setChallenges] = useState([]);
  const [showChallenge, setShowChallenge] = useState(false);

  const load = () => {
    const t = todayStr();
    api.get(`/social/friends?today=${t}`).then(({ data }) => setFriends(data)).catch(() => {});
    api.get("/social/requests").then(({ data }) => setRequests(data)).catch(() => {});
    api.get("/challenges").then(({ data }) => setChallenges(data)).catch(() => {});
  };

  useEffect(() => { load(); }, []);

  const respond = async (id, action) => {
    try {
      await api.post(`/social/requests/${id}/${action}`);
      toast.success(action === "accept" ? "Anfrage angenommen" : "Anfrage abgelehnt");
      load();
    } catch { toast.error("Fehlgeschlagen"); }
  };

  const gift = async (fid) => {
    try {
      await api.post(`/social/gift-freeze/${fid}`);
      toast.success("Eis verschenkt! ❄️");
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Fehlgeschlagen"); }
  };

  const respondChallenge = async (id, action) => {
    try {
      await api.post(`/challenges/${id}/${action}`);
      toast.success(action === "accept" ? "Challenge angenommen!" : "Challenge abgelehnt");
      load();
    } catch { toast.error("Fehlgeschlagen"); }
  };

  const pendingCount = requests.incoming.length;

  return (
    <div className="max-w-lg mx-auto px-5 pt-safe">
      <h1 className="font-display font-black text-4xl tracking-tighter mb-6">Freunde</h1>

      <Tabs defaultValue="friends" className="w-full">
        <TabsList className="w-full bg-[#121212] border border-white/10 rounded-full p-1 h-auto mb-5">
          <TabsTrigger value="friends" data-testid="tab-friends" className="flex-1 rounded-full py-2 data-[state=active]:bg-[#CCFF00] data-[state=active]:text-black text-neutral-400">Freunde</TabsTrigger>
          <TabsTrigger value="requests" data-testid="tab-requests" className="flex-1 rounded-full py-2 data-[state=active]:bg-[#CCFF00] data-[state=active]:text-black text-neutral-400">
            Anfragen{pendingCount > 0 ? ` (${pendingCount})` : ""}
          </TabsTrigger>
          <TabsTrigger value="challenges" data-testid="tab-challenges" className="flex-1 rounded-full py-2 data-[state=active]:bg-[#CCFF00] data-[state=active]:text-black text-neutral-400">Challenges</TabsTrigger>
        </TabsList>

        <TabsContent value="friends">
          <AddFriend onDone={load} />
          {friends.length === 0 ? (
            <p className="text-neutral-500 text-sm text-center py-6" data-testid="friends-empty">Noch keine Freunde. Teile deinen Link im Profil!</p>
          ) : (
            <div className="space-y-3" data-testid="friends-list">
              {friends.map((f) => (
                <div key={f.id} data-testid={`friend-${f.id}`} className="bg-[#121212] border border-white/10 rounded-2xl p-4 flex items-center gap-3">
                  <RouterLink to={`/u/${f.share_code}`} className="w-11 h-11 rounded-full bg-[#1A1A1A] border border-white/10 flex items-center justify-center font-display font-black text-lg shrink-0">
                    {f.name?.[0]?.toUpperCase()}
                  </RouterLink>
                  <div className="flex-1 min-w-0">
                    <div className="font-bold truncate">{f.name}</div>
                    <div className="flex items-center gap-3 text-xs text-neutral-500 mt-0.5">
                      <span className="flex items-center gap-1"><Flame className="w-3.5 h-3.5 text-[#CCFF00]" />{f.streak}</span>
                      <span className="flex items-center gap-1"><Snowflake className="w-3.5 h-3.5 text-[#66D9FF]" />{f.freezes}</span>
                      <span>{fmtDistance(f.weekly_distance_m)} km/Wo</span>
                    </div>
                  </div>
                  <button onClick={() => gift(f.id)} data-testid={`gift-${f.id}`} className="w-9 h-9 rounded-full bg-[#1A1A1A] border border-white/10 flex items-center justify-center text-[#66D9FF] active:scale-95 transition-transform" title="Eis schenken">
                    <Gift className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="requests">
          <div className="space-y-3" data-testid="requests-list">
            {requests.incoming.length === 0 && requests.outgoing.length === 0 && (
              <p className="text-neutral-500 text-sm text-center py-6">Keine offenen Anfragen.</p>
            )}
            {requests.incoming.map((r) => (
              <div key={r.id} data-testid={`incoming-${r.id}`} className="bg-[#121212] border border-white/10 rounded-2xl p-4 flex items-center gap-3">
                <div className="w-11 h-11 rounded-full bg-[#1A1A1A] border border-white/10 flex items-center justify-center font-display font-black text-lg shrink-0">{r.name?.[0]?.toUpperCase()}</div>
                <div className="flex-1 min-w-0">
                  <div className="font-bold truncate">{r.name}</div>
                  <div className="text-xs text-neutral-500">möchte dein Freund sein</div>
                </div>
                <button onClick={() => respond(r.id, "accept")} data-testid={`accept-${r.id}`} className="w-9 h-9 rounded-full bg-[#CCFF00] flex items-center justify-center active:scale-95 transition-transform"><Check className="w-5 h-5 text-black" /></button>
                <button onClick={() => respond(r.id, "decline")} data-testid={`decline-${r.id}`} className="w-9 h-9 rounded-full bg-[#1A1A1A] border border-white/10 flex items-center justify-center text-[#FF3366] active:scale-95 transition-transform"><X className="w-5 h-5" /></button>
              </div>
            ))}
            {requests.outgoing.map((r) => (
              <div key={r.id} className="bg-[#121212] border border-white/10 rounded-2xl p-4 flex items-center gap-3 opacity-70">
                <div className="w-11 h-11 rounded-full bg-[#1A1A1A] border border-white/10 flex items-center justify-center font-display font-black text-lg shrink-0">{r.name?.[0]?.toUpperCase()}</div>
                <div className="flex-1 min-w-0">
                  <div className="font-bold truncate">{r.name}</div>
                  <div className="text-xs text-neutral-500">Anfrage gesendet · wartet</div>
                </div>
                <button onClick={() => respond(r.id, "decline")} className="text-xs text-[#FF3366] font-bold">Zurückziehen</button>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="challenges">
          <button
            onClick={() => (friends.length ? setShowChallenge(true) : toast.error("Füge zuerst Freunde hinzu"))}
            data-testid="new-challenge-btn"
            className="w-full mb-5 bg-[#CCFF00] text-black font-display font-black rounded-full py-3.5 flex items-center justify-center gap-2 active:scale-95 transition-transform"
          >
            <Swords className="w-5 h-5" /> Challenge starten
          </button>
          {challenges.length === 0 ? (
            <p className="text-neutral-500 text-sm text-center py-6" data-testid="challenges-empty">Noch keine Challenges. Fordere einen Freund heraus!</p>
          ) : (
            <div className="space-y-3" data-testid="challenges-list">
              {challenges.map((c) => <ChallengeCard key={c.id} c={c} onRespond={respondChallenge} />)}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {showChallenge && <ChallengeModal friends={friends} onClose={() => setShowChallenge(false)} onCreated={() => { setShowChallenge(false); load(); }} />}
    </div>
  );
}

function ChallengeCard({ c, onRespond }) {
  const isDist = c.metric === "distance";
  const fmt = (v) => (isDist ? `${fmtDistance(v)} km` : fmtDuration(v));
  const meIsCreator = c.is_creator;
  const myProg = meIsCreator ? c.progress_creator : c.progress_opponent;
  const oppProg = meIsCreator ? c.progress_opponent : c.progress_creator;
  const oppName = meIsCreator ? c.opponent.name : c.creator.name;
  const total = (c.progress_creator || 0) + (c.progress_opponent || 0);
  const myPct = total > 0 ? (myProg / total) * 100 : 50;

  return (
    <div data-testid={`challenge-${c.id}`} className="bg-[#121212] border border-white/10 rounded-2xl p-4">
      <div className="flex items-center justify-between mb-3">
        <span className="flex items-center gap-1.5 font-bold text-sm">
          <Swords className="w-4 h-4 text-[#CCFF00]" /> Du vs. {oppName}
        </span>
        <span className={`text-xs font-bold uppercase tracking-wider px-2 py-1 rounded-full ${
          c.status === "active" ? "bg-[#CCFF00]/15 text-[#CCFF00]" : c.status === "pending" ? "bg-white/10 text-neutral-400" : "bg-white/5 text-neutral-500"
        }`}>{c.status === "active" ? "Läuft" : c.status === "pending" ? "Offen" : "Beendet"}</span>
      </div>

      {c.status === "pending" && !meIsCreator && (
        <div className="flex gap-2">
          <button onClick={() => onRespond(c.id, "accept")} data-testid={`challenge-accept-${c.id}`} className="flex-1 bg-[#CCFF00] text-black font-bold rounded-full py-2.5 active:scale-95 transition-transform">Annehmen</button>
          <button onClick={() => onRespond(c.id, "decline")} data-testid={`challenge-decline-${c.id}`} className="flex-1 bg-[#1A1A1A] border border-white/10 text-[#FF3366] font-bold rounded-full py-2.5 active:scale-95 transition-transform">Ablehnen</button>
        </div>
      )}
      {c.status === "pending" && meIsCreator && (
        <p className="text-xs text-neutral-500">Warte auf Annahme von {oppName}…</p>
      )}

      {(c.status === "active" || c.status === "completed") && (
        <>
          <div className="flex items-center justify-between text-sm mb-2">
            <span className="font-display font-black text-[#CCFF00]">{fmt(myProg)}</span>
            <span className="text-neutral-500 text-xs">{isDist ? "Distanz" : "Zeit"}</span>
            <span className="font-display font-black text-neutral-300">{fmt(oppProg)}</span>
          </div>
          <div className="h-2 rounded-full bg-[#1A1A1A] overflow-hidden flex">
            <div className="h-full bg-[#CCFF00]" style={{ width: `${myPct}%` }} />
            <div className="h-full bg-neutral-600" style={{ width: `${100 - myPct}%` }} />
          </div>
          {c.status === "completed" && (
            <p className="text-xs mt-3 font-bold" data-testid={`challenge-result-${c.id}`}>
              {c.winner === "tie" ? "🤝 Unentschieden!" : c.winner === (meIsCreator ? c.creator.id : c.opponent.id) ? "🏆 Du hast gewonnen!" : `${oppName} hat gewonnen`}
            </p>
          )}
        </>
      )}
    </div>
  );
}
